/**
 * 강화 확인 팝업(UpgradeConfirmPopup) 표시 데이터 — 순수(Phaser 무의존, 테스트 대상).
 * 레벨 diff 스탯 라인 + 등급별 ◀/▶ 비교 variants를 산출한다.
 */
import { t } from "../i18n/i18n.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import type { ModuleInfoData } from "./tabData.js";

export interface StatLine {
  readonly label: string;
  readonly current: string;
  readonly next: string;
}

/** 현재/다음 ModuleInfoData에서 스탯 diff 라인 산출 — 발동확률 + 각 effect 스탯("라벨 현재 → 다음"). */
export function buildStatLines(current: ModuleInfoData, next: ModuleInfoData): StatLine[] {
  const lines: StatLine[] = [];
  if (next.chancePercent !== undefined) {
    lines.push({
      label: t("moduleInfo.upgradeConfirm.chanceLabel"),
      current: `${current.chancePercent ?? 0}%`,
      next: `${next.chancePercent}%`,
    });
  }
  for (const ns of next.stats) {
    const cs = current.stats.find((s) => s.key === ns.key);
    lines.push({
      label: t(`moduleStat.${ns.key}`),
      current: `${cs?.value ?? "-"}`,
      next: `${ns.value}`,
    });
  }
  return lines;
}

/** 등급별 강화 비교 variant — 강화 확인 팝업의 ◀/▶ 전환용. */
export interface UpgradeVariant {
  readonly rarity: ModuleRarity;
  readonly statLines: readonly StatLine[];
}

/**
 * 강화 확인 팝업의 등급별 비교 variants 생성(순수). current/next(레벨 N·N+1)의 rarityNav를 등급별로 zip해
 * 각 등급의 "현재 레벨 → 다음 레벨" 스탯 비교를 만든다. rarityNav가 없으면(단일 등급 모듈) 보유 등급 1개만.
 * index는 보유(현재) 등급의 variants 내 위치 — 팝업이 여기서 시작한다.
 *
 * current/next는 같은 정의(def.effects) 기준이라 rarityNav.variants의 등급 집합·순서(RARITY_PROGRESSION)가
 * 동일하므로 인덱스 zip이 안전하다. 레벨은 등급과 무관한 moduleId 자산이라 모든 등급의 N→N+1이 같은 레벨 쌍.
 */
export function buildUpgradeVariants(
  current: ModuleInfoData,
  next: ModuleInfoData,
): { readonly variants: readonly UpgradeVariant[]; readonly index: number } {
  const cv = current.rarityNav?.variants ?? [current];
  const nv = next.rarityNav?.variants ?? [next];
  const n = Math.min(cv.length, nv.length);
  const variants: UpgradeVariant[] = [];
  for (let i = 0; i < n; i++) {
    variants.push({ rarity: cv[i]!.rarity, statLines: buildStatLines(cv[i]!, nv[i]!) });
  }
  return { variants, index: current.rarityNav?.index ?? 0 };
}
